import React, { useEffect, useMemo, useState } from "react";
import jwt_decode from "jwt-decode";
import { Card, Row, Col, Form, Button, Spinner, Table } from "react-bootstrap";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from "recharts";

import { getVentas } from "../helpers/rutaVentas";
import "../css/dashboard-financiero.css";

/* ----------------------------- UTILIDADES ----------------------------- */

const todayLocalISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const firstDayMonthISO = () => {
  const d = new Date();
  d.setDate(1);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const clean = (v) => String(v ?? "").trim();

const getRoleUsuario = () => {
  const token =
    localStorage.getItem("token") || "";

  try {
    if (!token) return "";

    const decoded = jwt_decode(token);

    return (
      decoded?.usuario?.role ||
      decoded?.user?.role ||
      decoded?.role ||
      ""
    );
  } catch {
    return "";
  }
};

const getSedeUsuario = () => {
  const token =
    localStorage.getItem("token") || "";

  try {
    if (!token) return "";

    const decoded = jwt_decode(token);

    const usuario =
      decoded?.usuario ||
      decoded?.user ||
      decoded ||
      {};

    return clean(usuario?.sede)
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toUpperCase()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  } catch {
    return "";
  }
};

const esVentaMiamiPropia = (
  venta = {}
) => {
  const sede = clean(venta?.sede)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return sede === "MIAMI";
};

const filtrarVentasReporteVendedores = (
  ventas = []
) => {
  const role = getRoleUsuario();
  const sede = getSedeUsuario();

  const esManagerMiami =
    role === "MANAGER_ROLE" &&
    sede === "MIAMI";

  if (!esManagerMiami) {
    return ventas;
  }

  return ventas.filter(
    (venta) =>
      esVentaMiamiPropia(venta)
  );
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const formatDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleDateString("es-AR");
};

const formatUSD = (value) => {
  const n = Number(value || 0);

  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const nombreUsuario = (usuario) => {
  if (!usuario) return "";
  if (typeof usuario === "string") return "";

  return (
    [usuario.nombres, usuario.apellidos].filter(Boolean).join(" ") ||
    [usuario.nombre, usuario.apellido].filter(Boolean).join(" ") ||
    usuario.email ||
    ""
  );
};

const nombreVendedorVenta = (venta = {}) => {
  return (
    clean(venta.vendedorNombre) ||
    clean(venta.usuarioNombre) ||
    clean(nombreUsuario(venta.vendedor)) ||
    clean(nombreUsuario(venta.usuario)) ||
    "SIN VENDEDOR"
  );
};

const nombreClienteCompra = (cliente = {}) => {
  if (!cliente) return "";

  return (
    clean(cliente.razonsocial) ||
    [cliente.apellido1, cliente.apellido2, cliente.nombre]
      .map(clean)
      .filter(Boolean)
      .join(" ") ||
    ""
  );
};

const getVentaId = (venta = {}) => {
  return clean(venta._id || venta.id || venta.nroventa);
};

const getItemsVenta = (venta = {}) => {
  return Array.isArray(venta.items) ? venta.items : [];
};

const getTotalVentaUSD = (venta = {}) => {
  const directo = Number(
    venta.totalUSD ??
      venta.totalVentaUSD ??
      venta.total ??
      venta.importeUSD ??
      venta.subtotalUSD ??
      0
  );

  if (Number.isFinite(directo) && directo > 0) return directo;

  const items = getItemsVenta(venta);

  return items.reduce((acc, item) => {
    const cantidad = Number(item.cantidad || 0);
    const precio = Number(item.precioVentaUSD || item.precioUSD || 0);
    const envio = Number(item.costoEnvioUSD || 0);

    return acc + cantidad * precio + cantidad * envio;
  }, 0);
};

const getComisionVentaUSD = (venta = {}) => {
  return Number(
    venta.comisionUSD ??
      venta.comision ??
      venta.totalComisionUSD ??
      venta.montoComisionUSD ??
      0
  );
};

const getCantidadProductosVenta = (venta = {}) => {
  const items = getItemsVenta(venta);

  return items.reduce((acc, item) => {
    return acc + Number(item.cantidad || 0);
  }, 0);
};

const getCantidadRegistrosVenta = (venta = {}) => {
  return getItemsVenta(venta).length;
};

const normalizar = (v = "") =>
  String(v || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const exportarCSV = (resumen = [], desde = "", hasta = "") => {
  const rows = [
    [
      "Vendedor",
      "Ventas",
      "Productos",
      "Registros",
      "Total vendido USD",
      "Comision USD",
      "Ticket promedio USD",
      "% del total",
    ],
    ...resumen.map((r) => [
      r.vendedor,
      r.cantidadVentas,
      r.cantidadProductos,
      r.cantidadRegistros,
      r.totalUSD.toFixed(2),
      r.comisionUSD.toFixed(2),
      r.ticketPromedioUSD.toFixed(2),
      r.porcentaje.toFixed(2),
    ]),
  ];

  const csv = rows
    .map((row) =>
      row
        .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
        .join(";")
    )
    .join("\n");

  const blob = new Blob([csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = `reporte-vendedores-${desde || "desde"}-${hasta || "hasta"}.csv`;
  a.click();

  URL.revokeObjectURL(url);
};

/* ----------------------------- COMPONENTE ----------------------------- */

export default function ReporteVendedoresVentas() {
  const [desde, setDesde] = useState(firstDayMonthISO());
  const [hasta, setHasta] = useState(todayLocalISO());
  const [estadoVenta, setEstadoVenta] = useState("");
  const [q, setQ] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ventas, setVentas] = useState([]);

  const cargarReporte = async () => {
    setLoading(true);
    setError("");

    try {
      const LIMIT = 200;
      let page = 1;
      let total = 0;
      let acumulado = [];

      do {
        const resp = await getVentas({
          page,
          limit: LIMIT,
          q: "",
          estado: estadoVenta,
          desde,
          hasta,
        });

        const lote = Array.isArray(resp?.ventas) ? resp.ventas : [];

        acumulado = acumulado.concat(lote);
        total = Number(resp?.total || lote.length || 0);
        page += 1;
      } while (acumulado.length < total);

        acumulado =
          filtrarVentasReporteVendedores(
            acumulado
          );

        const map = new Map();

        acumulado.forEach((venta) => {
        const id = getVentaId(venta);
        if (!id) return;
        if (!map.has(id)) map.set(id, venta);
      });

      setVentas(Array.from(map.values()));
    } catch (err) {
      console.error("❌ Error cargando reporte vendedores:", err);

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error al cargar informe de vendedores."
      );

      setVentas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarReporte();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ventasFiltradas = useMemo(() => {
    const texto = normalizar(q);

    if (!texto) return ventas;

    return ventas.filter((venta) => {
      const cadena = [
        venta.nroventa,
        nombreVendedorVenta(venta),
        nombreClienteCompra(venta.clienteCompra),
        venta.estado,
        venta.observaciones,
      ]
        .map(normalizar)
        .join(" ");

      return cadena.includes(texto);
    });
  }, [ventas, q]);

  const resumenVendedores = useMemo(() => {
    const map = new Map();

    ventasFiltradas.forEach((venta) => {
      const vendedor = nombreVendedorVenta(venta);
      const key = normalizar(vendedor) || "SIN VENDEDOR";

      if (!map.has(key)) {
        map.set(key, {
          vendedor,
          cantidadVentas: 0,
          cantidadProductos: 0,
          cantidadRegistros: 0,
          totalUSD: 0,
          comisionUSD: 0,
        });
      }

      const row = map.get(key);

      row.cantidadVentas += 1;
      row.cantidadProductos += getCantidadProductosVenta(venta);
      row.cantidadRegistros += getCantidadRegistrosVenta(venta);
      row.totalUSD += getTotalVentaUSD(venta);
      row.comisionUSD += getComisionVentaUSD(venta);
    });

    const totalGeneral = Array.from(map.values()).reduce(
      (acc, row) => acc + Number(row.totalUSD || 0),
      0
    );

    return Array.from(map.values())
      .map((row) => ({
        ...row,
        totalUSD: round2(row.totalUSD),
        comisionUSD: round2(row.comisionUSD),
        ticketPromedioUSD:
          row.cantidadVentas > 0
            ? round2(row.totalUSD / row.cantidadVentas)
            : 0,
        porcentaje:
          totalGeneral > 0
            ? round2((row.totalUSD / totalGeneral) * 100)
            : 0,
      }))
      .sort((a, b) => b.totalUSD - a.totalUSD);
  }, [ventasFiltradas]);

  const totales = useMemo(() => {
    return resumenVendedores.reduce(
      (acc, row) => {
        acc.vendedores += 1;
        acc.ventas += row.cantidadVentas;
        acc.productos += row.cantidadProductos;
        acc.registros += row.cantidadRegistros;
        acc.totalUSD += row.totalUSD;
        acc.comisionUSD += row.comisionUSD;

        return acc;
      },
      {
        vendedores: 0,
        ventas: 0,
        productos: 0,
        registros: 0,
        totalUSD: 0,
        comisionUSD: 0,
      }
    );
  }, [resumenVendedores]);

  const topVendedores = useMemo(() => {
    return resumenVendedores.slice(0, 12);
  }, [resumenVendedores]);

  const productosPorVendedor = useMemo(() => {
    return resumenVendedores
      .filter((row) => Number(row.cantidadProductos || 0) > 0)
      .slice(0, 12);
  }, [resumenVendedores]);

  const seriePorDia = useMemo(() => {
    const map = new Map();

    ventasFiltradas.forEach((venta) => {
      const fecha = formatDate(venta.fecha) || "-";

      if (!map.has(fecha)) {
        map.set(fecha, {
          fecha,
          ventas: 0,
          productos: 0,
          total: 0,
        });
      }

      const row = map.get(fecha);

      row.ventas += 1;
      row.productos += getCantidadProductosVenta(venta);
      row.total += getTotalVentaUSD(venta);
    });

    return Array.from(map.values())
      .map((row) => ({
        ...row,
        total: round2(row.total),
      }))
      .sort((a, b) => {
        const [da, ma, ya] = a.fecha.split("/");
        const [db, mb, yb] = b.fecha.split("/");

        return new Date(`${ya}-${ma}-${da}`) - new Date(`${yb}-${mb}-${db}`);
      });
  }, [ventasFiltradas]);

  const limpiarFiltros = () => {
    setDesde(firstDayMonthISO());
    setHasta(todayLocalISO());
    setEstadoVenta("");
    setQ("");
  };

  return (
    <div className="dashboard-financiero dashboard-light container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-1">Informe de Ventas por Vendedor</h3>
          <small className="text-muted">
            Totales entre fechas para control y liquidación de comisiones.
          </small>
        </div>

        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={limpiarFiltros}
          >
            Limpiar
          </Button>

          <Button
            variant="dark"
            size="sm"
            onClick={cargarReporte}
            disabled={loading}
          >
            {loading ? "Buscando..." : "Buscar"}
          </Button>

          <Button
            variant="success"
            size="sm"
            onClick={() => exportarCSV(resumenVendedores, desde, hasta)}
            disabled={loading || resumenVendedores.length === 0}
          >
            CSV
          </Button>
        </div>
      </div>

      <Row className="g-2 mb-3">
        <Col md={2}>
          <Form.Label>Desde</Form.Label>
          <Form.Control
            type="date"
            size="sm"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
        </Col>

        <Col md={2}>
          <Form.Label>Hasta</Form.Label>
          <Form.Control
            type="date"
            size="sm"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
        </Col>

        <Col md={2}>
          <Form.Label>Estado venta</Form.Label>
          <Form.Select
            size="sm"
            value={estadoVenta}
            onChange={(e) => setEstadoVenta(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="PENDIENTE">PENDIENTE</option>
            <option value="CONFIRMADA">CONFIRMADA</option>
            <option value="ENTREGADA">ENTREGADA</option>
            <option value="ANULADA">ANULADA</option>
          </Form.Select>
        </Col>

        <Col md={6}>
          <Form.Label>Buscar vendedor / cliente / venta</Form.Label>
          <Form.Control
            type="text"
            size="sm"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="vendedor, cliente, número de venta..."
          />
        </Col>
      </Row>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Row xs={1} md={2} xl={5} className="g-3 mb-3 dashboard-kpis">
            <Col>
              <Card className="dashboard-kpi">
                <Card.Body>
                  <div className="kpi-icon kpi-blue">👥</div>
                  <small>Vendedores</small>
                  <h4>{totales.vendedores}</h4>
                </Card.Body>
              </Card>
            </Col>

            <Col>
              <Card className="dashboard-kpi">
                <Card.Body>
                  <div className="kpi-icon kpi-green">🧾</div>
                  <small>Ventas</small>
                  <h4>{totales.ventas}</h4>
                </Card.Body>
              </Card>
            </Col>

            <Col>
              <Card className="dashboard-kpi">
                <Card.Body>
                  <div className="kpi-icon kpi-yellow">📦</div>
                  <small>Productos</small>
                  <h4>{totales.productos}</h4>
                </Card.Body>
              </Card>
            </Col>

            <Col>
              <Card className="dashboard-kpi">
                <Card.Body>
                  <div className="kpi-icon kpi-purple">US$</div>
                  <small>Total vendido</small>
                  <h4>{formatUSD(totales.totalUSD)}</h4>
                </Card.Body>
              </Card>
            </Col>

            <Col>
              <Card className="dashboard-kpi">
                <Card.Body>
                  <div className="kpi-icon kpi-blue">💰</div>
                  <small>Comisión</small>
                  <h4>{formatUSD(totales.comisionUSD)}</h4>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <Card className="dashboard-panel">
                <Card.Body>
                  <h6>Ventas por día</h6>

                  <div className="dashboard-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={seriePorDia}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="fecha" />
                        <YAxis />
                        <Tooltip
                          formatter={(value, name) =>
                            name === "total" ? formatUSD(value) : value
                          }
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="ventas"
                          name="Ventas"
                          stroke="#3b82f6"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="productos"
                          name="Productos"
                          stroke="#22c55e"
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="dashboard-panel">
                <Card.Body>
                  <h6>Top vendedores por total</h6>

                  <div className="dashboard-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={topVendedores}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="vendedor" />
                        <YAxis />
                        <Tooltip formatter={(value) => formatUSD(value)} />
                        <Bar dataKey="totalUSD" name="Total" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="dashboard-panel">
                <Card.Body>
                  <h6>Productos por vendedor</h6>

                  <div className="dashboard-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={productosPorVendedor}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="vendedor" />
                        <YAxis />
                        <Tooltip />
                        <Bar
                          dataKey="cantidadProductos"
                          name="Productos"
                          fill="#facc15"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col md={12}>
              <Card className="dashboard-panel">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">
                      Resumen de vendedores entre fechas
                    </h6>

                    <small className="text-muted">
                      Ordenado de mayor a menor total vendido.
                    </small>
                  </div>

                  <div className="table-responsive">
                    <Table size="sm" hover>
                      <thead>
                        <tr>
                          <th style={{ width: 45 }}>#</th>
                          <th>Vendedor</th>
                          <th className="text-end">Ventas</th>
                          <th className="text-end">Productos</th>
                          <th className="text-end">Registros</th>
                          <th className="text-end">Total vendido</th>
                          <th className="text-end">Comisión</th>
                          <th className="text-end">Ticket prom.</th>
                          <th className="text-end">% total</th>
                        </tr>
                      </thead>

                      <tbody>
                        {resumenVendedores.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="text-center py-4">
                              No hay ventas para los filtros seleccionados.
                            </td>
                          </tr>
                        ) : (
                          resumenVendedores.map((row, idx) => (
                            <tr key={row.vendedor}>
                              <td>{idx + 1}</td>
                              <td>
                                <strong>{row.vendedor}</strong>
                              </td>
                              <td className="text-end">
                                {row.cantidadVentas}
                              </td>
                              <td className="text-end">
                                {row.cantidadProductos}
                              </td>
                              <td className="text-end">
                                {row.cantidadRegistros}
                              </td>
                              <td className="text-end">
                                <strong>{formatUSD(row.totalUSD)}</strong>
                              </td>
                              <td className="text-end">
                                {formatUSD(row.comisionUSD)}
                              </td>
                              <td className="text-end">
                                {formatUSD(row.ticketPromedioUSD)}
                              </td>
                              <td className="text-end">
                                {row.porcentaje.toFixed(1)}%
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>

                      {resumenVendedores.length > 0 && (
                        <tfoot>
                          <tr>
                            <th colSpan="2">Totales</th>
                            <th className="text-end">{totales.ventas}</th>
                            <th className="text-end">{totales.productos}</th>
                            <th className="text-end">{totales.registros}</th>
                            <th className="text-end">
                              {formatUSD(totales.totalUSD)}
                            </th>
                            <th className="text-end">
                              {formatUSD(totales.comisionUSD)}
                            </th>
                            <th className="text-end">-</th>
                            <th className="text-end">100%</th>
                          </tr>
                        </tfoot>
                      )}
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}